import React from 'react';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import WavingHandOutlinedIcon from '@mui/icons-material/WavingHandOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import StatCard from '../components/StatCard';
import ChartBlock from '../components/ChartBlock';
import TimelineChart from '../components/TimelineChart';
import DonutChart from '../components/DonutChart';
import RankList from '../components/RankList';
import DistrictMapBlock from '../components/districtMap/DistrictMapBlock';
import styles from '../components/stats.module.css';
import palette from '../components/palette';
import PatientConfig from '../../../consts/PatientConfig';
import { formatNumber, formatPercent, granularityHint } from '../utils/format';
import { HELP } from '../utils/help';

export const PATIENT_CARDS = [
  { key: 'new', label: 'Cadastros novos', icon: <PersonAddAltOutlinedIcon />, accent: 'teal' },
  { key: 'seen', label: 'Pacientes atendidos', icon: <PeopleAltOutlinedIcon />, accent: 'blue', help: HELP.seen },
  { key: 'firstVisit', label: 'Primeira consulta', icon: <WavingHandOutlinedIcon />, accent: 'purple', hint: 'primeiro atendimento na clínica' },
  { key: 'returning', label: 'Voltaram', icon: <ReplayOutlinedIcon />, accent: 'green' },
  { key: 'returnRate', label: 'Taxa de retorno', icon: <LoyaltyOutlinedIcon />, type: 'percent', accent: 'green', hint: 'atendidos que já tinham vindo antes' },
  { key: 'visitsPerPatient', label: 'Consultas por paciente', icon: <RepeatOutlinedIcon />, type: 'decimal', accent: 'amber' },
];

const AGE_LABELS = {
  '0-1': 'Até 1 ano',
  '2-11': '2 a 11 anos',
  '12-17': '12 a 17 anos',
  '18-29': '18 a 29 anos',
  '30-44': '30 a 44 anos',
  '45-59': '45 a 59 anos',
  '60+': '60 anos ou mais',
  unknown: 'Sem data de nascimento',
};

export const genderItems = (gender) => gender.map((item) => ({
  label: PatientConfig.GetGenderById(item.key)?.Label || 'Não informado',
  value: item.count,
}));

export const ageItems = (age) => age
  .filter((item) => item.count > 0)
  .map((item) => ({ label: AGE_LABELS[item.key] || item.key, value: item.count }));

const SINCE_LABELS = { '3-6': '3 a 6 meses', '6-9': '6 a 9 meses', '9-12': '9 a 12 meses' };
const VISITS_LABELS = { 1: 'Só 1 consulta', '2-3': '2 a 3 consultas', '4-9': '4 a 9 consultas', '10+': '10 ou mais' };

const bucketItems = (list, labels) => list.map((item) => ({ label: labels[item.key] || item.key, value: item.count }));

/** Quem parou de vir, só em números: quanto pesa, há quanto tempo e depois de quantas consultas. */
export const InactivePatients = ({ inactive }) => {
  if (!inactive || inactive.total === 0) {
    return <div className={styles.emptyChart}>Nenhum paciente sumido: todos os que vieram no último ano voltaram ou têm horário marcado.</div>;
  }
  const once = inactive.visits.find((item) => String(item.key) === '1');
  return (
    <>
      <div className={`${styles.highlight} ${styles.highlightNeutral}`}>
        <PersonOffOutlinedIcon />
        <span>
          {`${formatNumber(inactive.total)} pacientes (${formatPercent(inactive.rate)} de quem veio no último ano) estão há mais de 3 meses sem consulta e sem nada marcado`}
          {once && once.count > 0 ? `; ${formatNumber(once.count)} deles vieram só uma vez.` : '.'}
        </span>
      </div>
      <div className={`${styles.rowEven} ${styles.inactiveRow}`}>
        <div>
          <span className={styles.subTitle}>Há quanto tempo não vêm</span>
          <RankList items={bucketItems(inactive.since, SINCE_LABELS)} color={palette.amber} />
        </div>
        <div>
          <span className={styles.subTitle}>Consultas que tinham feito</span>
          <RankList items={bucketItems(inactive.visits, VISITS_LABELS)} color={palette.blue} />
        </div>
      </div>
    </>
  );
};

const PatientsSection = ({ data, granularity, period }) => (
  <>
    <div className={styles.grid}>
      {PATIENT_CARDS.map((card) => <StatCard key={card.key} {...card} kpi={data.kpis[card.key]} />)}
    </div>

    <div className={styles.row}>
      <ChartBlock title="Novos e atendidos" hint={granularityHint(granularity)}>
        <TimelineChart
          data={data.timeline}
          granularity={granularity}
          series={[
            { key: 'seen', label: 'Atendidos', color: palette.blue },
            { key: 'new', label: 'Cadastros novos', color: palette.teal },
          ]}
        />
      </ChartBlock>
      <ChartBlock title="Como chegaram" hint="cadastros novos">
        <DonutChart
          items={data.origins.map((item) => ({ label: item.label, value: item.count }))}
          centerLabel="novos"
          empty="Nenhum paciente novo no período."
        />
      </ChartBlock>
    </div>

    <div className={styles.rowEven}>
      <ChartBlock title="Faixa etária" hint="pacientes atendidos">
        <RankList items={ageItems(data.profile.age)} color={palette.purple} />
      </ChartBlock>
      <ChartBlock title="Sexo" hint="pacientes atendidos">
        <DonutChart items={genderItems(data.profile.gender)} centerLabel="pacientes" />
      </ChartBlock>
    </div>

    <div className={styles.rowEven}>
      <ChartBlock title="Bairros" hint={data.profile.mainCity ? `pacientes atendidos, a maioria de ${data.profile.mainCity}` : 'pacientes atendidos'}>
        <RankList
          items={data.profile.district.map((item) => ({ label: item.label, value: item.count }))}
          color={palette.teal}
          empty="Nenhum bairro preenchido nos cadastros dos pacientes atendidos."
        />
      </ChartBlock>
      <ChartBlock title="Queixas mais comuns">
        <RankList
          items={data.complaints.map((item) => ({ label: item.label, value: item.count }))}
          color={palette.pink}
          empty="Nenhuma queixa registrada no período."
        />
      </ChartBlock>
    </div>

    <DistrictMapBlock map={data.map} period={period} />

    <ChartBlock
      title="Pacientes que pararam de vir"
      hint="vieram no último ano e não voltaram"
    >
      <InactivePatients inactive={data.inactive} />
    </ChartBlock>
  </>
);

export default PatientsSection;
